import { useMemo } from "react"
import type { FC } from "react"
import { Stack, useMediaQuery, useTheme } from "@mui/material"
import { GaugeContainer } from "@mui/x-charts"

import type { GaugePlotView, RecordType } from "types/dashboard.types"
import useGaugeConfig from "widgets/plots/configuration/useGaugeConfig"
import MuiGaugeBgArc from "./MuiGaugeBgArc"
import MuiGaugeColoredArc from "./MuiGaugeColoredArc"
import { ANGULAR_OVERSHOOT_DEG } from "./helpers"
import MuiGaugeTicks from "./MuiGaugeTicks"
import MuiGaugeLabel from "./MuiGaugeLabel"
import MuiGaugePointer from "./MuiGaugePointer"

interface MuiGaugeChartProps {
  view: GaugePlotView
  data: RecordType[]
}

const MuiGaugeChart: FC<MuiGaugeChartProps> = ({ view, data }) => {
  const theme = useTheme()
  const isXs = useMediaQuery("(max-width:374px)")
  const isDownSm = useMediaQuery(theme.breakpoints.down("sm"))
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"))
  const config = useGaugeConfig(view, data)
  const { cellBG, decimalPlaces, domain, fill, plotSize, range, ticks, values } = config

  const size = useMemo(() => {
    if (isXs) return plotSize.xs
    if (isDownSm) return plotSize.sm
    if (isDownMd) return plotSize.md
    return plotSize.lg
  }, [isXs, isDownSm, isDownMd, plotSize])

  const startAngle = range.min
  const endAngle = range.max

  const clampedValue = Math.max(domain.min, Math.min(domain.max, values.value))

  // Angles in radians (MUI convention: 0 = 12 o'clock, clockwise).
  const startAngleRad = (range.min * Math.PI) / 180
  const endAngleRad = (range.max * Math.PI) / 180

  const innerRadius = size / 3.5
  const outerRadius = size / 3.5 + size / 13

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      aria-label={`${fill.name}-gauge`}
      sx={{ width: "100%", height: "100%", "& svg": { overflow: "visible" } }}
    >
      <GaugeContainer
        width={size}
        height={size}
        cx="50%"
        cy="50%"
        value={clampedValue}
        valueMin={domain.min}
        valueMax={domain.max}
        startAngle={startAngle - ANGULAR_OVERSHOOT_DEG}
        endAngle={endAngle + ANGULAR_OVERSHOOT_DEG}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
      >
        <MuiGaugeBgArc
          color={cellBG.fill}
          filter={cellBG.filter}
          startAngleRad={startAngleRad}
          endAngleRad={endAngleRad}
        />
        <MuiGaugeColoredArc
          colorGradient={fill.gradient.colorGradient}
          actualValue={clampedValue}
          domainMin={domain.min}
          domainMax={domain.max}
          startAngleRad={startAngleRad}
          endAngleRad={endAngleRad}
        />
        <MuiGaugeTicks
          ticks={ticks}
          size={size}
          startAngleRad={startAngleRad}
          endAngleRad={endAngleRad}
          domainMin={domain.min}
          domainMax={domain.max}
        />
        <MuiGaugePointer
          actualValue={clampedValue}
          domainMin={domain.min}
          domainMax={domain.max}
          startAngleRad={startAngleRad}
          endAngleRad={endAngleRad}
        />
        <MuiGaugeLabel values={values} size={size} decimalPlaces={decimalPlaces} />
      </GaugeContainer>
    </Stack>
  )
}

export default MuiGaugeChart
